import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Grid,
} from '@mui/material';
import { LocationOn as LocationIcon } from '@mui/icons-material';

declare global {
  interface Window {
    google: any;
    initGoogleMaps: () => void;
  }
}

export interface AddressData {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  latitude?: number;
  longitude?: number;
}

interface GoogleMapsAddressInputProps {
  value: AddressData;
  onChange: (address: AddressData) => void;
  error?: Record<string, string>;
  disabled?: boolean;
}

export const GoogleMapsAddressInput: React.FC<GoogleMapsAddressInputProps> = ({
  value,
  onChange,
  error = {},
  disabled = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);



  const mapRef = useRef<HTMLDivElement>(null);
  const autocompleteRef = useRef<any>(null);
  const mapInstanceRef = useRef<any>(null);
  const markerRef = useRef<any>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load Google Maps API

  const initializeMap = useCallback(() => {
    if (!mapRef.current || !window.google) return;

    try {
      // Initialize map
      const defaultCenter = value.latitude && value.longitude
        ? { lat: value.latitude, lng: value.longitude }
        : { lat: 37.7749, lng: -122.4194 }; // Default to San Francisco

      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        center: defaultCenter,
        zoom: value.latitude && value.longitude ? 15 : 10,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });

      // Initialize autocomplete
      if (inputRef.current) {
        autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
          types: ['address'],
          fields: ['address_components', 'formatted_address', 'geometry'],
        });

        autocompleteRef.current.addListener('place_changed', handlePlaceSelect);
      }

      // Add click listener to map
      mapInstanceRef.current.addListener('click', handleMapClick);

      // Add existing marker if coordinates exist
      if (value.latitude && value.longitude) {
        addMarker(value.latitude, value.longitude);
      }

      // Set initial input value if street address exists
      if (inputRef.current && value.street) {
        const fullAddress = [
          value.street,
          value.city,
          value.state,
          value.zipCode,
          value.country
        ].filter(Boolean).join(', ');
        inputRef.current.value = fullAddress;
      }

      setIsMapReady(true);
      setApiError(null);
    } catch (error) {
      console.error('Error initializing map:', error);
      setApiError('Failed to initialize map');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const loadGoogleMapsAPI = useCallback(async () => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      setApiError('Google Maps API key not configured');
      return;
    }

    if (window.google && window.google.maps) {
      initializeMap();
      return;
    }

    setIsLoading(true);

    try {
      // Create callback function
      window.initGoogleMaps = () => {
        initializeMap();
        setIsLoading(false);
      };

      // Check if script already exists
      if (!document.querySelector('script[src*="maps.googleapis.com"]')) {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&callback=initGoogleMaps`;
        script.async = true;
        script.defer = true;
        script.onerror = () => {
          setApiError('Failed to load Google Maps API');
          setIsLoading(false);
        };
        document.head.appendChild(script);
      }
    } catch (error) {
      console.error('Error loading Google Maps API:', error);
      setApiError('Failed to load Google Maps API');
      setIsLoading(false);
    }
  }, [initializeMap]);

  useEffect(() => {
    loadGoogleMapsAPI();
  }, [loadGoogleMapsAPI]);

  const handlePlaceSelect = () => {
    const place = autocompleteRef.current.getPlace();
    console.log('Selected place:', place);

    if (!place.geometry) {
      console.warn('No geometry found for place');
      return;
    }

    const addressData = parseAddressComponents(place);
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();

    // Update address data
    const newAddress: AddressData = {
      ...addressData,
      latitude: lat,
      longitude: lng,
    };

    console.log('Updating address with:', newAddress);
    onChange(newAddress);

    // Update input field with formatted address
    if (inputRef.current && place.formatted_address) {
      inputRef.current.value = place.formatted_address;
    }

    // Update map
    mapInstanceRef.current.setCenter({ lat, lng });
    mapInstanceRef.current.setZoom(15);
    addMarker(lat, lng);
  };

  const handleMapClick = (event: any) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();

    // Reverse geocode to get address
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results: any[], status: any) => {
      if (status === 'OK' && results[0]) {
        const addressData = parseAddressComponents(results[0]);
        const newAddress: AddressData = {
          ...addressData,
          latitude: lat,
          longitude: lng,
        };

        onChange(newAddress);

        // Update input field
        if (inputRef.current) {
          inputRef.current.value = results[0].formatted_address;
        }
      }
    });

    addMarker(lat, lng);
  };

  const addMarker = (lat: number, lng: number) => {
    // Remove existing marker
    if (markerRef.current) {
      markerRef.current.setMap(null);
    }

    // Add new marker
    markerRef.current = new window.google.maps.Marker({
      position: { lat, lng },
      map: mapInstanceRef.current,
      draggable: true,
    });

    // Add drag listener
    markerRef.current.addListener('dragend', (event: any) => {
      const newLat = event.latLng.lat();
      const newLng = event.latLng.lng();

      onChange({
        ...value,
        latitude: newLat,
        longitude: newLng,
      });
    });
  };

  const parseAddressComponents = (place: any): Omit<AddressData, 'latitude' | 'longitude'> => {
    const components: Omit<AddressData, 'latitude' | 'longitude'> = {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
    };

    if (!place.address_components) {
      console.warn('No address_components found in place:', place);
      return components;
    }

    console.log('Parsing address components:', place.address_components);

    let streetNumber = '';
    let route = '';

    place.address_components.forEach((component: any) => {
      const types = component.types;
      console.log('Component:', component.long_name, 'Types:', types);

      // Street components
      if (types.includes('street_number')) {
        streetNumber = component.long_name;
      } else if (types.includes('route')) {
        route = component.long_name;
      } else if (types.includes('premise')) {
        // Building name or number
        if (!streetNumber) streetNumber = component.long_name;
      }

      // City components (priority order)
      else if (types.includes('locality')) {
        components.city = component.long_name;
      } else if (types.includes('administrative_area_level_2') && !components.city) {
        // District/County level (common in India)
        components.city = component.long_name;
      } else if (types.includes('administrative_area_level_3') && !components.city) {
        // Sub-district level
        components.city = component.long_name;
      } else if (types.includes('sublocality_level_1') && !components.city) {
        // Neighborhood level (fallback)
        components.city = component.long_name;
      } else if (types.includes('sublocality') && !components.city) {
        // General sublocality
        components.city = component.long_name;
      }

      // State/Province components
      else if (types.includes('administrative_area_level_1')) {
        // State/Province level
        components.state = component.long_name; // Use long_name for better readability
      }

      // Postal code
      else if (types.includes('postal_code')) {
        components.zipCode = component.long_name;
      }

      // Country
      else if (types.includes('country')) {
        components.country = component.long_name;
      }
    });

    // Combine street number and route
    if (streetNumber && route) {
      components.street = `${streetNumber} ${route}`;
    } else if (route) {
      components.street = route;
    } else if (streetNumber) {
      components.street = streetNumber;
    }

    // Fallback logic for missing components
    if (!components.city && place.formatted_address) {
      console.log('No city found, trying to extract from formatted address:', place.formatted_address);

      // Try to find a better city from other administrative levels
      place.address_components.forEach((component: any) => {
        const types = component.types;
        if (!components.city) {
          if (types.includes('administrative_area_level_4') ||
            types.includes('administrative_area_level_5') ||
            types.includes('neighborhood') ||
            types.includes('political')) {
            components.city = component.long_name;
          }
        }
      });
    }

    // Ensure we have at least some meaningful data
    if (!components.street && place.name && place.name !== place.formatted_address) {
      components.street = place.name;
    }

    console.log('Parsed components:', components);
    return components;
  };

  const handleManualChange = (field: keyof AddressData, newValue: string) => {
    onChange({
      ...value,
      [field]: newValue,
    });
  };

  if (apiError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {apiError}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Property Address
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Search for your address or click on the map to set the exact location.
      </Typography>

      {isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Loading Google Maps...</Typography>
        </Box>
      )}

      <Grid container spacing={3}>
        {/* Address Search Input */}
        <Grid item xs={12}>
          <TextField
            inputRef={inputRef}
            fullWidth
            label="Search Address"
            placeholder="Start typing your address..."
            disabled={disabled || !isMapReady}
            error={!!error['address.street']}
            helperText={error['address.street'] || 'Search for your address or click on the map'}
            InputProps={{
              startAdornment: (
                <LocationIcon sx={{ color: 'text.secondary', mr: 1 }} />
              ),
            }}
          />
        </Grid>

        {/* Interactive Map */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Interactive Map
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Click on the map to set the exact location. You can drag the marker to fine-tune the position.
            </Typography>
            <Box
              ref={mapRef}
              sx={{
                width: '100%',
                height: 300,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'grey.100',
              }}
            />
          </Paper>
        </Grid>

        {/* Manual Address Fields */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>
            Address Details
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Street Address"
            value={value.street}
            onChange={(e) => handleManualChange('street', e.target.value)}
            error={!!error['address.street']}
            helperText={error['address.street']}
            disabled={disabled}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="City"
            value={value.city}
            onChange={(e) => handleManualChange('city', e.target.value)}
            error={!!error['address.city']}
            helperText={error['address.city']}
            disabled={disabled}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            label="State"
            value={value.state}
            onChange={(e) => handleManualChange('state', e.target.value)}
            error={!!error['address.state']}
            helperText={error['address.state']}
            disabled={disabled}
          />
        </Grid>

        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            label="ZIP Code"
            value={value.zipCode}
            onChange={(e) => handleManualChange('zipCode', e.target.value)}
            error={!!error['address.zipCode']}
            helperText={error['address.zipCode']}
            disabled={disabled}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Country"
            value={value.country}
            onChange={(e) => handleManualChange('country', e.target.value)}
            error={!!error['address.country']}
            helperText={error['address.country']}
            disabled={disabled}
          />
        </Grid>



        {/* Coordinates Display */}
        {value.latitude && value.longitude && (
          <Grid item xs={12}>
            <Paper sx={{ p: 2, backgroundColor: 'success.light', color: 'success.contrastText' }}>
              <Typography variant="body2">
                📍 Location set: {value.latitude.toFixed(6)}, {value.longitude.toFixed(6)}
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default GoogleMapsAddressInput;